// EP-141 T141-001 — the WHEN half of the merged /timesheet page. Moved from
// the retired StaffCalendarPage.tsx (EP-133 T133-023), which owned its own
// week nav + staff selector; both are gone here on purpose. TimesheetPage now
// owns the single week selection and the single staff filter for the whole
// page (grid + hours table); this component only renders shifts/visits for
// the week/columns it's handed.
//
// Q-1 CORRECTED (EP-141 §9, 2026-09-01): visits do NOT render inside a staff
// lane. `visitor_visits` carries no staff/user column at all (verified
// against schema.rb — account/property/unit/visitor/visitor_widget +
// timestamps only), so a visit cannot be attributed to a person's lane. They
// render as day-scoped dashed bands spanning the full day column instead.
import { useMemo, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui'
import { Loader2 } from 'lucide-react'
import type { ShiftBlock, CalendarVisit } from '@/hooks/useWeekSchedule'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { zonedDateTimeParts, formatTime as formatTimeInZone } from '@/lib/date'

// Earliest hour shown on the grid. 06:00 is generous for any shift that
// starts before the property opens (e.g., a 07:00 cleaning crew).
const GRID_START_HOUR = 6
// 30-minute slots per hour.
const SLOTS_PER_HOUR = 2
// 14-hour total grid (06:00 to 20:00) = 28 slots.
const TOTAL_SLOTS = 14 * SLOTS_PER_HOUR
const SLOT_MINUTES = 30
const SLOT_HEIGHT_PX = 28 // 28px per 30-min slot = 56px per hour — comfortable on mobile

// A day column is at most 120 px wide on a desktop (owner, 2026-10-07). This
// replaces T141-018's 90 px lane per roster member, which made a day 630 px
// wide with seven staff: shifts no longer sit in a lane per person (each
// block carries its person's name), they take the whole column and split it
// only where they overlap in time, the way a calendar lays out events. The
// horizontal scroll stays on the Card, so the PAGE never scrolls sideways.
const DAY_COLUMN_WIDTH_PX = 120
const TIME_COLUMN_WIDTH_PX = 64 // 4rem, matches the time-gutter column below

// Monday-first, matching TimesheetPage's own `days` array (both pages built
// their week arrays starting Monday, independently — see the T141-001 survey).
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

export interface TimesheetWeekGridColumn {
  id: number
  name: string
}

export interface TimesheetWeekGridProps {
  /** Exactly 7 dates, Monday first — the same array driving the hours table. */
  weekDays: Date[]
  shifts: ShiftBlock[]
  visits: CalendarVisit[]
  /**
   * One lane per entry, in display order. T141-001 survey (a): this must come
   * from the account roster (the same source the hours table rows from), not
   * be derived from which users happen to have a shift this week — otherwise
   * a staff member with an empty week gets no lane while still appearing as a
   * zero row in the table below.
   */
  staffColumns: TimesheetWeekGridColumn[]
  isLoading: boolean
}

// isSameDay compares two Dates that were BOTH built via local Date arithmetic
// (weekDays[i], activeDay, `new Date()`) — no ISO-string parsing involved, so
// local getters are correct and unrelated to the starts_at note below.
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

// DAT-1: `weekDays[i]` is built via local Date arithmetic (local midnight),
// same as isSameDay's Dates above: read it back with local getters, never
// `toISOString().slice(0, 10)`. For a runtime ahead of UTC (e.g. Asia/Bangkok,
// UTC+7) local midnight is still the PREVIOUS UTC calendar day, so the
// data-testids below would always name the day before the one actually
// rendered.
function toLocalDateString(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// ---------------------------------------------------------------------------
// starts_at / ends_at are GENUINE UTC instants (T141-015 — the backend now
// parses shift_block_times in the account's configured timezone, not under
// config.time_zone = "UTC" wall-clock-as-UTC). This means the standing
// CLAUDE.md rule applies again, unremarkably: store UTC, render in the
// account timezone. Everything below reads the account timezone from
// useLocalizationConfig() and threads it through slotIndex/slotSpan/
// formatTimeInZone/isSameWallClockDay/groupByDay.
//
// This grid PREVIOUSLY (T141-013, 2026-09-01) read starts_at/ends_at with
// getUTC* getters, because at the time starts_at was wall-clock-stored-as-UTC
// (a data-model bug, not a display choice) and that was the deliberate,
// narrower fix pending the real one. T141-015 landed the real one — the
// backend fix plus a backfill — so the getUTC* workaround is gone. Do not
// reintroduce getUTCHours()/getUTCMinutes() here: starts_at is a real instant
// now, and reading it with UTC getters would show a UTC+7 account's 08:00
// shift at 01:00. Do not use plain getHours()/getMinutes() either — that's
// browser-local, right only by accident for an operator sitting in the same
// zone as the account, per the same standing rule.
// ---------------------------------------------------------------------------

// Returns the slot index (0..TOTAL_SLOTS-1) for an ISO timestamp, in the
// given IANA timezone, or -1 if outside the visible window.
function slotIndex(iso: string, timezone: string): number {
  const { hour, minute } = zonedDateTimeParts(iso, timezone)
  const minutesFromStart = (hour - GRID_START_HOUR) * 60 + minute
  return Math.max(-1, Math.floor(minutesFromStart / SLOT_MINUTES))
}

// Returns the duration in slots between two ISO timestamps, clamped to the
// visible window.
function slotSpan(startIso: string, endIso: string, timezone: string): number {
  const start = slotIndex(startIso, timezone)
  const end = slotIndex(endIso, timezone)
  if (start < 0 || start >= TOTAL_SLOTS) return 0
  return Math.max(1, Math.min(end - start, TOTAL_SLOTS - start))
}

// A shift starting "...T16:30:00.000Z" belongs on whichever calendar day
// that instant falls on IN THE ACCOUNT'S TIMEZONE (e.g. 23:30 in Bangkok,
// same day; but a UTC 23:30 shift would land on the NEXT Bangkok day) — not
// the day it happens to fall on in the browser's own zone.
function isSameWallClockDay(iso: string, day: Date, timezone: string): boolean {
  const { year, month, day: zonedDay } = zonedDateTimeParts(iso, timezone)
  return year === day.getFullYear() && month === day.getMonth() + 1 && zonedDay === day.getDate()
}

function groupByDay<T extends { starts_at: string }>(
  items: T[],
  weekDays: Date[],
  timezone: string,
): Map<string, T[]> {
  const map = new Map<string, T[]>()
  for (const item of items) {
    const key = weekDays.find((w) => isSameWallClockDay(item.starts_at, w, timezone))?.toDateString()
    if (!key) continue
    const list = map.get(key) ?? []
    list.push(item)
    map.set(key, list)
  }
  return map
}

interface PlacedShift {
  block: ShiftBlock
  start: number
  span: number
  /** Side-by-side column within its group of overlapping shifts. */
  col: number
  /** How many side-by-side columns that group needs. */
  cols: number
}

// Calendar layout for one day: shifts are taken in start order; a run of
// shifts that overlap one another in time (a group) shares the column side by
// side, each in the first sub-column free at its start; a shift that overlaps
// nothing gets the whole width.
function placeShifts(blocks: ShiftBlock[], timezone: string): PlacedShift[] {
  const items = blocks
    .map((block) => ({ block, start: slotIndex(block.starts_at, timezone), span: slotSpan(block.starts_at, block.ends_at, timezone) }))
    .filter((item) => item.start >= 0 && item.span > 0)
    .sort((a, b) => a.start - b.start || b.span - a.span)
  const placed: PlacedShift[] = []
  let group: PlacedShift[] = []
  let colEnds: number[] = []
  let groupEnd = -1
  const closeGroup = () => {
    for (const p of group) p.cols = colEnds.length
    placed.push(...group)
    group = []
    colEnds = []
  }
  for (const item of items) {
    if (group.length > 0 && item.start >= groupEnd) closeGroup()
    let col = colEnds.findIndex((end) => end <= item.start)
    if (col < 0) {
      col = colEnds.length
      colEnds.push(0)
    }
    colEnds[col] = item.start + item.span
    group.push({ ...item, col, cols: 1 })
    groupEnd = Math.max(groupEnd, item.start + item.span)
  }
  closeGroup()
  return placed
}

export function TimesheetWeekGrid({ weekDays, shifts, visits, staffColumns, isLoading }: TimesheetWeekGridProps) {
  const { t } = useTranslation('timesheet')
  // Default to today if it's in the visible week, otherwise the first day —
  // the original StaffCalendarPage always defaulted to `new Date()` even when
  // that fell outside the displayed week (see T141-001 handoff notes); this
  // is a small robustness improvement to that same initial-mount case only,
  // not a fix for switching weeks afterwards (out of scope here).
  const [activeDay, setActiveDay] = useState<Date>(
    () => weekDays.find((d) => isSameDay(d, new Date())) ?? weekDays[0]
  )

  const isMobile = useIsMobile()
  const { timezone } = useLocalizationConfig()

  const shiftsByDay = useMemo(() => groupByDay(shifts, weekDays, timezone), [shifts, weekDays, timezone])
  const visitsByDay = useMemo(() => groupByDay(visits, weekDays, timezone), [visits, weekDays, timezone])

  // One width, shared by the header row's grid template, the slots grid
  // template and DayColumn's own root width, so the three stay aligned.
  const dayColWidth = DAY_COLUMN_WIDTH_PX

  return (
    <div className="space-y-3">
      {isMobile ? (
        <Card compact className="p-3">
          <div className="flex gap-2 overflow-x-auto pb-2" data-testid="calendar-day-strip">
            {weekDays.map((day, i) => {
              const active = isSameDay(day, activeDay)
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => setActiveDay(day)}
                  className={`flex-shrink-0 px-3 py-2 rounded-md text-center min-w-[3.5rem] ${
                    active ? 'bg-primary text-primary-content' : 'bg-base-200 text-base-content'
                  }`}
                  data-testid={`calendar-day-${toLocalDateString(day)}`}
                  data-active={active}
                >
                  <div className="text-xs">{t(`days.${DAY_KEYS[i]}`)}</div>
                  <div className="text-sm font-semibold tabular-nums">{day.getDate()}</div>
                </button>
              )
            })}
          </div>

          {/* On a phone the one day shown takes the card's width. */}
          <div>
            <DayColumn
              day={activeDay}
              shifts={shiftsByDay.get(activeDay.toDateString()) ?? []}
              visits={visitsByDay.get(activeDay.toDateString()) ?? []}
              staffColumns={staffColumns}
              timezone={timezone}
            />
          </div>
        </Card>
      ) : (
        <Card compact className="w-fit max-w-full overflow-x-auto p-0">
          <div style={{ minWidth: `${TIME_COLUMN_WIDTH_PX + 7 * dayColWidth}px` }}>
            <div className="grid border-b bg-base-200 sticky top-0 z-10" style={{ gridTemplateColumns: `${TIME_COLUMN_WIDTH_PX}px repeat(7, ${dayColWidth}px)` }}>
              <div className="border-r" />
              {weekDays.map((day, i) => (
                <div key={day.toISOString()} className="px-2 py-2 text-center border-r last:border-r-0">
                  <div className="text-xs text-base-content/60">{t(`days.${DAY_KEYS[i]}`)}</div>
                  <div className="text-sm font-semibold tabular-nums">{day.getDate()}</div>
                </div>
              ))}
            </div>
            <div className="grid" style={{ gridTemplateColumns: `${TIME_COLUMN_WIDTH_PX}px repeat(7, ${dayColWidth}px)` }}>
              {/* Time column */}
              <div className="border-r">
                {Array.from({ length: 14 }).map((_, hour) => (
                  <div
                    key={hour}
                    className="text-xs text-base-content/50 text-right pr-2 tabular-nums"
                    style={{ height: `${SLOT_HEIGHT_PX * 2}px` }}
                  >
                    {String(GRID_START_HOUR + hour).padStart(2, '0')}:00
                  </div>
                ))}
              </div>
              {/* Day columns */}
              {weekDays.map((day) => (
                <DayColumn
                  key={day.toISOString()}
                  day={day}
                  shifts={shiftsByDay.get(day.toDateString()) ?? []}
                  visits={visitsByDay.get(day.toDateString()) ?? []}
                  staffColumns={staffColumns}
                  timezone={timezone}
                  width={dayColWidth}
                />
              ))}
            </div>
          </div>
        </Card>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-primary" aria-label="loading" />
        </div>
      )}

      {/* Legend. T141-017 — owner-chosen vocabulary is "Entry", not "Shift",
          even here, where "Shift" had been doing real contrastive work
          against "Visit" (a record type vs. an event type). Flagged to the
          owner before this change; they still chose Entry. */}
      <div className="flex items-center gap-3 text-xs text-base-content/60">
        <span className="inline-flex items-center gap-1">
          <span className="block w-3 h-3 rounded bg-primary/30 border border-primary" />
          {t('legend_entry')}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="block w-3 h-3 rounded border-2 border-dashed border-secondary" />
          {t('legend_visit')}
        </span>
      </div>
    </div>
  )
}

function DayColumn({
  day,
  shifts,
  visits,
  staffColumns,
  timezone,
  width,
}: {
  day: Date
  shifts: ShiftBlock[]
  visits: CalendarVisit[]
  staffColumns: TimesheetWeekGridColumn[]
  timezone: string
  /** Fixed px width on a desktop; absent on a phone, where it fills the card. */
  width?: number
}) {
  // The staff filter: only the people the page is showing have their shifts drawn.
  const shown = new Set(staffColumns.map((c) => c.id))
  const placed = placeShifts(shifts.filter((b) => shown.has(b.user_id)), timezone)
  return (
    <div
      className={`border-r last:border-r-0 relative ${width ? '' : 'w-full'}`}
      style={width ? { width: `${width}px` } : undefined}
      data-testid={`calendar-column-${toLocalDateString(day)}`}
    >
      {/* Slot gridlines */}
      {Array.from({ length: TOTAL_SLOTS }).map((_, i) => (
        <div
          key={i}
          className={`absolute inset-x-0 border-t ${i % 2 === 0 ? 'border-base-200' : 'border-base-200/50'}`}
          style={{ top: `${i * SLOT_HEIGHT_PX}px`, height: `${SLOT_HEIGHT_PX}px` }}
        />
      ))}
      {/* Container for absolute-positioned cards */}
      <div className="relative" style={{ height: `${TOTAL_SLOTS * SLOT_HEIGHT_PX}px` }}>
        {/* Visits render first (day-scoped dashed bands, full width, no lane —
            Q-1 corrected) so shift lanes paint on top of them in normal DOM
            stacking order, keeping shift text legible where the two overlap. */}
        {visits.map((visit) => {
          const start = slotIndex(visit.starts_at, timezone)
          const span = slotSpan(visit.starts_at, visit.ends_at, timezone)
          if (start < 0 || span === 0) return null
          return (
            <div
              key={visit.id}
              className="absolute inset-x-0 rounded-md border-2 border-dashed border-secondary bg-secondary/10 text-xs px-2 py-1 overflow-hidden"
              style={{
                top: `${start * SLOT_HEIGHT_PX}px`,
                height: `${span * SLOT_HEIGHT_PX}px`,
              }}
              data-testid={`calendar-visit-${visit.id}`}
              data-status={visit.status}
              title={`${visit.visitor.first_name} ${visit.visitor.last_name} • ${visit.property_name}${visit.unit_name ? ` · ${visit.unit_name}` : ''}`}
            >
              <p className="font-medium truncate">{visit.visitor.first_name} {visit.visitor.last_name}</p>
              <p className="text-[10px] truncate">{visit.property_name}{visit.unit_name ? ` · ${visit.unit_name}` : ''}</p>
            </div>
          )
        })}
        {placed.map(({ block, start, span, col, cols }) => (
          <div
            key={block.id}
            className="absolute rounded-md bg-primary/20 border border-primary text-xs px-1.5 py-1 overflow-hidden"
            style={{
              top: `${start * SLOT_HEIGHT_PX}px`,
              height: `${span * SLOT_HEIGHT_PX}px`,
              left: `calc(${(col * 100) / cols}% + 1px)`,
              width: `calc(${100 / cols}% - 2px)`,
            }}
            data-testid={`shift-block-${block.id}`}
            title={`${block.user_name} • ${formatTimeInZone(block.starts_at, { timezone })}–${formatTimeInZone(block.ends_at, { timezone })}`}
          >
            <p className="font-medium truncate">{block.user_name}</p>
            <p className="text-[10px] tabular-nums truncate">{formatTimeInZone(block.starts_at, { timezone })}–{formatTimeInZone(block.ends_at, { timezone })}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// Media-query hook via useSyncExternalStore — the store IS the external
// system, so no setState-in-effect is needed and the first render already
// reads the real match (react-hooks/set-state-in-effect). No shared
// equivalent exists elsewhere in web/src/hooks (checked during the T141-001
// survey), so this stays local to the grid, same as it was in
// StaffCalendarPage.
const MOBILE_QUERY = '(max-width: 767px)'
function subscribeMobile(callback: () => void): () => void {
  const mql = window.matchMedia(MOBILE_QUERY)
  mql.addEventListener('change', callback)
  return () => mql.removeEventListener('change', callback)
}
function useIsMobile(): boolean {
  return useSyncExternalStore(
    subscribeMobile,
    () => window.matchMedia(MOBILE_QUERY).matches,
    () => false
  )
}
